import { useRef, useEffect, useCallback, useMemo } from 'react'
import Globe, { GlobeMethods } from 'react-globe.gl'
import * as h3 from 'h3-js'
import type { MonitorEventItem, MonitorLiveVessel, MonitorLiveAircraft, GammaIrradiatorFacility, MilitaryBase } from '@/lib/api'
import { getSpreadEventCoordinates } from '@/components/monitor/eventCoordinateSpread'
import { getNearestCountryCode } from '@/components/monitor/countryCoords'

interface EnrichedPoint {
  latitude: number
  longitude: number
  event: MonitorEventItem
  title: string
  severity: string
  domain: string
  source: string
  country_name?: string | null
}

interface VesselPoint {
  type: 'vessel'
  latitude: number
  longitude: number
  mmsi: string
  name: string | null
  speed: number | null
  course: number | null
  destination: string | null
  status: string | null
}

interface FlightPoint {
  type: 'flight'
  latitude: number
  longitude: number
  icao24: string
  callsign: string | null
  origin_country: string | null
  velocity: number | null
  true_track: number | null
  baro_altitude: number | null
}

interface GpsJamPoint {
  type: 'gpsjam'
  latitude: number
  longitude: number
  h3Index: string
  n_unique_ac: number
  n_bad: number
  ratio_bad: number
  altitude: string
}

interface GammaPoint {
  type: 'gamma'
  latitude: number
  longitude: number
  id: string
  city: string
  country: string
  facility: GammaIrradiatorFacility
}

interface BasePoint {
  type: 'base'
  latitude: number
  longitude: number
  id: string
  name: string
  country: string
  baseType: string
  status?: string
  base: MilitaryBase
}

type GlobePoint = EnrichedPoint | VesselPoint | FlightPoint | GpsJamPoint | GammaPoint | BasePoint

function isEnrichedPoint(d: GlobePoint): d is EnrichedPoint {
  return 'event' in d
}

const MILITARY_CALLSIGN_PATTERNS = /^(RCH|REACH|EVAC|VIPER|EAGLE|DUKE|NAVY|HUSKY|BLUE|COBRA|SNAKE|TIGER|BONE|SPAR|ROOK|MASH|DUSTOFF|MOOSE|HERKY|CALLSIGN|PAE|OMF|PAT|HAWK|RAPTOR|STRIKE|WOLF|BEAR|LION|HOG)/i
const MILITARY_COUNTRIES = ['RU', 'CN', 'KP', 'IR', 'SY', 'US', 'GB', 'FR', 'DE', 'IL', 'IN', 'PK', 'SA']

function isMilitaryAircraft(fp: FlightPoint): boolean {
  const cs = (fp.callsign || '').toUpperCase()
  const country = (fp.origin_country || '').toUpperCase()
  return MILITARY_CALLSIGN_PATTERNS.test(cs) || MILITARY_COUNTRIES.includes(country)
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#ff2255',
  high: '#ff6b2b',
  medium: '#eab308',
  low: '#3b82f6',
  info: '#6b7280',
}

const ARC_TYPE_COLORS: Record<string, { main: string; trail: string }> = {
  ransomware: { main: '#ff0040', trail: '#ff004040' },
  vulnerability: { main: '#a855f7', trail: '#a855f740' },
  cyber_attack: { main: '#06b6d4', trail: '#06b6d440' },
  // FalconFeeds-derived categories
  ff_ddos: { main: '#22d3ee', trail: '#22d3ee40' },
  ff_breach: { main: '#facc15', trail: '#facc1540' },
  ff_ransomware: { main: '#ec4899', trail: '#ec489940' },
  conflict: { main: '#ff6600', trail: '#ff660040' },
  disaster: { main: '#f59e0b', trail: '#f59e0b40' },
  default: { main: '#64748b', trail: '#64748b40' },
}

export interface ArcData {
  startLat: number
  startLng: number
  endLat: number
  endLng: number
  color: string
  color2: string
  label: string
  severity: string
  stroke: number
  eventType: string
  dashLen: number
  dashGap: number
  animateTime: number
  /** Optional attached event for click/selection */
  event?: MonitorEventItem | null
}

export interface RingData {
  lat: number
  lng: number
  maxR: number
  propagationSpeed: number
  repeatPeriod: number
  color: string
}

interface GlobeViewProps {
  events: MonitorEventItem[]
  arcs: ArcData[]
  rings: RingData[]
  selectedEvent: MonitorEventItem | null
  onEventClick: (event: MonitorEventItem) => void
  maxZoom?: number
  colorBy?: 'severity' | 'domain'
  globeRefCallback?: (ref: GlobeMethods | undefined) => void
  vessels?: MonitorLiveVessel[]
  flights?: MonitorLiveAircraft[]
  flightArcs?: ArcData[]
  selectedAircraft?: MonitorLiveAircraft | null
  onAircraftClick?: (aircraft: MonitorLiveAircraft) => void
  onVesselClick?: (vessel: MonitorLiveVessel) => void
  /** Optional: open a country intel view for the given country code */
  onCountryClick?: (countryCode: string) => void
  onGammaClick?: (facility: GammaIrradiatorFacility) => void
  onBaseClick?: (base: MilitaryBase) => void
  gammaFacilities?: GammaIrradiatorFacility[]
  militaryBases?: MilitaryBase[]
  gpsJamCells?: import('@/lib/api').GpsJamJammingCell[]
  gpsSpoofingArcs?: ArcData[]
}

const VESSEL_COLOR = '#3b82f6'
const FLIGHT_COLOR = '#22c55e'
const MILITARY_FLIGHT_COLOR = '#ef4444'

const FLIGHT_ICON = '✈'
const VESSEL_ICON = '🚢'
const GAMMA_ICON = '☢'
const BASE_ICON = '🎯'

export default function GlobeView({
  events,
  arcs,
  rings,
  selectedEvent,
  onEventClick,
  maxZoom = 5,
  colorBy = 'severity',
  globeRefCallback,
  vessels = [],
  flights = [],
  flightArcs = [],
  selectedAircraft = null,
  onAircraftClick,
  onVesselClick,
  onCountryClick,
  onGammaClick,
  onBaseClick,
  gammaFacilities = [],
  militaryBases = [],
  gpsJamCells = [],
  gpsSpoofingArcs = [],
}: GlobeViewProps) {
  const globeRef = useRef<GlobeMethods | undefined>(undefined)

  /** Use shared spread so events with same coords (e.g. same country) get distinct nodes. */
  const enrichedPoints = useMemo<EnrichedPoint[]>(() => {
    const spreadMap = getSpreadEventCoordinates(events)
    return events
      .filter((e) => spreadMap.has(e.id))
      .map((e) => {
        const { lat, lng } = spreadMap.get(e.id)!
        return {
          latitude: lat,
          longitude: lng,
          event: e,
          title: e.title,
          severity: e.severity,
          domain: e.domain,
          source: e.source,
          country_name: e.country_name,
        }
      })
  }, [events])

  const flightPoints = useMemo<FlightPoint[]>(() => {
    return flights
      .filter((f) => f.latitude != null && f.longitude != null)
      .slice(0, 250)
      .map((f) => ({
        type: 'flight' as const,
        latitude: f.latitude!,
        longitude: f.longitude!,
        icao24: f.icao24,
        callsign: f.callsign ?? null,
        origin_country: f.origin_country ?? null,
        velocity: f.velocity ?? null,
        true_track: f.true_track ?? null,
        baro_altitude: f.baro_altitude ?? null,
      }))
  }, [flights])

  const gpsJamPoints = useMemo<GpsJamPoint[]>(() => {
    const pts: GpsJamPoint[] = []
    for (const cell of gpsJamCells) {
      const h3Index = cell.h3_indices?.[0] || cell.id
      if (!h3Index) continue
      try {
        const [lat, lng] = h3.cellToLatLng(h3Index)
        pts.push({
          type: 'gpsjam',
          latitude: lat,
          longitude: lng,
          h3Index,
          n_unique_ac: cell.n_unique_ac,
          n_bad: cell.n_bad,
          ratio_bad: cell.ratio_bad,
          altitude: cell.altitude || '',
        })
      } catch {
        // skip invalid H3 indices
      }
    }
    return pts.slice(0, 500)
  }, [gpsJamCells])

  const gpsJamHeatmapData = useMemo(() => {
    if (gpsJamPoints.length === 0) return []
    return [{ points: gpsJamPoints }]
  }, [gpsJamPoints])

  const gpsJamHeatmapColorFn = useCallback((t: number) => {
    const opacity = 0.2 + 0.65 * Math.min(1, t)
    const r = Math.round(220 + 35 * Math.min(1, t))
    const g = Math.round(38 * (1 - Math.min(1, t)))
    const b = Math.round(38 * (1 - Math.min(1, t)))
    return `rgba(${r},${g},${b},${opacity})`
  }, [])

  const allPoints = useMemo<GlobePoint[]>(() => {
    const pts: GlobePoint[] = [...enrichedPoints]
    // Add a clickable node at each arc end so arcs without a coincident event point are still clickable
    arcs.forEach((a) => {
      if (a.event && a.endLat != null && a.endLng != null) {
        const ev = a.event
        const hasExact = enrichedPoints.some(
          (p) => (p as EnrichedPoint).event?.id === ev.id && Math.abs((p as EnrichedPoint).latitude - a.endLat!) < 0.01 && Math.abs((p as EnrichedPoint).longitude - a.endLng!) < 0.01
        )
        if (!hasExact) {
          pts.push({
            latitude: a.endLat,
            longitude: a.endLng,
            event: ev,
            title: ev.title,
            severity: ev.severity ?? 'high',
            domain: ev.domain ?? 'cyber',
            source: ev.source ?? '',
            country_name: ev.country_name ?? null,
            country_code: ev.country_code ?? null,
          } as EnrichedPoint)
        }
      }
    })
    vessels.forEach((v) => {
      if (v.latitude != null && v.longitude != null) {
        pts.push({
          type: 'vessel',
          latitude: v.latitude,
          longitude: v.longitude,
          mmsi: v.mmsi,
          name: v.name ?? null,
          speed: v.speed ?? null,
          course: v.course ?? null,
          destination: v.destination ?? null,
          status: v.status ?? null,
        })
      }
    })
    flightPoints.forEach((fp) => pts.push(fp))
    gpsJamPoints.forEach((gp) => pts.push(gp))
    gammaFacilities.forEach((f) => {
      pts.push({
        type: 'gamma',
        latitude: f.lat,
        longitude: f.lon,
        id: f.id,
        city: f.city,
        country: f.country,
        facility: f,
      })
    })
    militaryBases.forEach((b) => {
      pts.push({
        type: 'base',
        latitude: b.lat,
        longitude: b.lon,
        id: b.id,
        name: b.name,
        country: b.country,
        baseType: b.type,
        status: b.status,
        base: b,
      })
    })
    return pts
  }, [enrichedPoints, vessels, flightPoints, gpsJamPoints, gammaFacilities, militaryBases, arcs])

  const initialPOVSet = useRef(false)

  useEffect(() => {
    const globe = globeRef.current
    if (!globe) return

    const controls = globe.controls() as any
    if (controls) {
      controls.autoRotate = false
      controls.autoRotateSpeed = 0.3
      controls.maxDistance = 600
      controls.minDistance = 120 / maxZoom
    }

    // Set initial view only once so zoom-to-location is not overwritten on re-render
    if (!initialPOVSet.current) {
      globe.pointOfView({ lat: 20, lng: 10, altitude: 2.5 }, 0)
      initialPOVSet.current = true
    }

    const scene = globe.scene()
    if (scene) {
      scene.fog = null
    }

    globeRefCallback?.(globe)
  }, [maxZoom, globeRefCallback])

  useEffect(() => {
    if (!selectedEvent || !selectedEvent.latitude || !selectedEvent.longitude) return
    const controls = globeRef.current?.controls() as any
    if (controls) controls.autoRotate = false
    globeRef.current?.pointOfView(
      { lat: selectedEvent.latitude, lng: selectedEvent.longitude, altitude: 1.5 },
      800
    )
  }, [selectedEvent])

  const getColor = useCallback(
    (d: object) => {
      const pt = d as GlobePoint
      if (pt && 'type' in pt && pt.type === 'vessel') return VESSEL_COLOR
      if (pt && 'type' in pt && pt.type === 'flight') return FLIGHT_COLOR
      if (pt && 'type' in pt && pt.type === 'gamma') return '#eab308'
      if (pt && 'type' in pt && pt.type === 'base') return '#f97316'
      const ep = pt as EnrichedPoint
      if (colorBy === 'severity') return SEVERITY_COLORS[ep.severity] || '#6b7280'
      const isCritical = ep.severity === 'critical'
      switch (ep.domain) {
        case 'cyber':          return isCritical ? '#d946ef' : '#a855f7'
        case 'geopolitical':   return isCritical ? '#ff2255' : '#ef4444'
        case 'infrastructure': return isCritical ? '#ff6600' : '#f59e0b'
        default:               return '#6b7280'
      }
    },
    [colorBy]
  )

  const getRadius = useCallback((d: object) => {
    const pt = d as GlobePoint
    if (pt && 'type' in pt) {
      if (pt.type === 'vessel') return 0.12
      if (pt.type === 'flight') return selectedAircraft && pt.icao24 === selectedAircraft.icao24 ? 0.16 : 0.12
      if (pt.type === 'gpsjam') return 0.14
      if (pt.type === 'gamma') return 0.12
      if (pt.type === 'base') return 0.13
    }
    const ev = pt as EnrichedPoint
    switch (ev.severity) {
      case 'critical': return 0.2
      case 'high': return 0.16
      case 'medium': return 0.14
      case 'low': return 0.12
      default: return 0.1
    }
  }, [selectedAircraft])

  const getLabel = useCallback((d: object) => {
    const pt = d as GlobePoint
    if (pt && 'type' in pt) {
      if (pt.type === 'flight') return flightLabel(pt)
      if (pt.type === 'gpsjam') {
        const gp = pt as GpsJamPoint
        const badPct = (gp.ratio_bad * 100).toFixed(1)
        return `<div style="background:rgba(0,0,0,0.95);color:#fff;padding:8px 12px;border-radius:4px;font-size:10px;border:1px solid #dc2626;font-family:monospace">
        <div style="color:#dc2626;font-weight:700">GPS JAM</div>
        <div>${gp.n_unique_ac} aircraft · ${gp.n_bad} bad · ${badPct}%</div>
        <div style="opacity:0.6;font-size:8px">${gp.altitude}</div>
      </div>`
      }
      if (pt.type === 'vessel') {
        const name = pt.name || pt.mmsi
        const status = pt.status ? ` · ${pt.status}` : ''
        return `<div style="background:rgba(0,0,0,0.95);color:#fff;padding:8px 12px;border-radius:4px;font-size:10px;border:1px solid ${VESSEL_COLOR};font-family:monospace">
        <div style="color:${VESSEL_COLOR};font-weight:700">VESSEL</div>
        <div>${name}${status}</div>
      </div>`
      }
      if (pt.type === 'gamma') {
        const g = pt as GammaPoint
        return `<div style="background:rgba(0,0,0,0.95);color:#fff;padding:8px 12px;border-radius:4px;font-size:10px;border:1px solid #eab308;font-family:monospace">
        <div style="color:#eab308;font-weight:700">GAMMA IRRADIATOR</div>
        <div>${g.city || ''}${g.country ? ' · ' + g.country : ''}</div>
      </div>`
      }
      if (pt.type === 'base') {
        const b = pt as BasePoint
        return `<div style="background:rgba(0,0,0,0.95);color:#fff;padding:8px 12px;border-radius:4px;font-size:10px;border:1px solid #f97316;font-family:monospace">
        <div style="color:#f97316;font-weight:700">MILITARY BASE</div>
        <div>${b.name}</div>
        <div style="opacity:0.7;font-size:9px">${b.country || ''}${b.baseType ? ' · ' + b.baseType : ''}${b.status ? ' · ' + b.status : ''}</div>
      </div>`
      }
    }
    const ep = pt as EnrichedPoint
    const color = SEVERITY_COLORS[ep.severity] || '#444'
    const glowIntensity = ep.severity === 'critical' ? '0 0 30px ' + color + ',0 0 80px ' + color + '80' : ep.severity === 'high' ? '0 0 20px ' + color + '90' : '0 0 12px ' + color + '60'
    return `<div style="background:rgba(0,0,0,0.95);color:#fff;padding:10px 14px;border-radius:8px;font-size:11px;max-width:300px;border:2px solid ${color};box-shadow:${glowIntensity};font-family:monospace;cursor:pointer">
      <div style="font-weight:700;margin-bottom:4px;color:${color};letter-spacing:1px;font-size:10px">${ep.severity.toUpperCase()} — ${ep.domain.toUpperCase()} ●</div>
      <div style="margin-bottom:6px;line-height:1.4;font-family:sans-serif">${ep.title.slice(0, 140)}</div>
      <div style="opacity:0.6;font-size:9px;border-top:1px solid ${color}40;padding-top:4px;margin-top:4px">${ep.source}${ep.country_name ? ' · ' + ep.country_name : ''}</div>
    </div>`
  }, [])

  const handlePointClick = useCallback(
    (d: object) => {
      const pt = d as GlobePoint
      if (isEnrichedPoint(pt)) {
        const ev = pt.event
        // If we know the country, prefer opening the country intel view so user sees all attacks there
        if (onCountryClick && ev.country_code) {
          onCountryClick(ev.country_code)
        } else {
          onEventClick(ev)
        }
      } else if (pt && 'type' in pt && pt.type === 'flight' && onAircraftClick) {
        const fp = pt as FlightPoint
        onAircraftClick({
          icao24: fp.icao24,
          callsign: fp.callsign,
          latitude: fp.latitude,
          longitude: fp.longitude,
          velocity: fp.velocity,
          true_track: fp.true_track,
          baro_altitude: fp.baro_altitude,
          origin_country: fp.origin_country,
        })
      } else if (pt && 'type' in pt && pt.type === 'vessel' && onVesselClick) {
        const vp = pt as VesselPoint
        onVesselClick({
          mmsi: vp.mmsi,
          name: vp.name,
          latitude: vp.latitude,
          longitude: vp.longitude,
          speed: vp.speed,
          course: vp.course,
          destination: vp.destination,
          status: vp.status,
        })
      } else if (pt && 'type' in pt && pt.type === 'gamma' && onGammaClick && 'facility' in pt) {
        onGammaClick((pt as GammaPoint).facility)
      } else if (pt && 'type' in pt && pt.type === 'base' && onBaseClick && 'base' in pt) {
        onBaseClick((pt as BasePoint).base)
      } else if (pt && 'type' in pt && (pt.type === 'gamma' || pt.type === 'base') && onCountryClick) {
        const code = getNearestCountryCode(pt.latitude, pt.longitude)
        if (code) onCountryClick(code)
      }
    },
    [onEventClick, onAircraftClick, onVesselClick, onCountryClick, onGammaClick, onBaseClick]
  )

  const getTooltipTextForIcon = useCallback((pt: GlobePoint): string => {
    if (pt && 'type' in pt) {
      if (pt.type === 'flight') return (pt.callsign || pt.icao24) + (pt.origin_country ? ` · ${pt.origin_country}` : '')
      if (pt.type === 'vessel') return pt.name || pt.mmsi
      if (pt.type === 'gamma') return `${(pt as GammaPoint).city || ''} ${(pt as GammaPoint).country || ''}`.trim() || 'Gamma irradiator'
      if (pt.type === 'base') return (pt as BasePoint).name
      if (pt.type === 'gpsjam') return `GPS jam · ${(pt as GpsJamPoint).n_unique_ac} aircraft · ${(pt as GpsJamPoint).n_bad} bad`
    }
    return (pt as EnrichedPoint).title || ''
  }, [])

  // HTML icons for flights, vessels, gamma facilities, and military bases (cyber stays as glowing dots)
  const createIconElement = useCallback(
    (d: object): HTMLElement | null => {
      const pt = d as GlobePoint
      if (!('type' in pt) || (pt.type !== 'flight' && pt.type !== 'vessel' && pt.type !== 'gamma' && pt.type !== 'base')) return null
      let icon: string
      let color: string
      if (pt.type === 'flight') {
        icon = FLIGHT_ICON
        color = isMilitaryAircraft(pt as FlightPoint) ? MILITARY_FLIGHT_COLOR : FLIGHT_COLOR
      } else if (pt.type === 'vessel') {
        icon = VESSEL_ICON
        color = VESSEL_COLOR
      } else if (pt.type === 'gamma') {
        icon = GAMMA_ICON
        color = '#eab308'
      } else {
        icon = BASE_ICON
        color = '#f97316'
      }

      const el = document.createElement('div')
      el.className = 'globe-marker-icon'
      el.title = getTooltipTextForIcon(pt)
      el.style.cssText = `
        display:flex;align-items:center;justify-content:center;
        width:26px;height:26px;border-radius:9999px;
        background:${color}15;border:2px solid ${color};
        box-shadow:0 0 12px ${color}80,0 0 30px ${color}40;
        font-size:13px;cursor:pointer;user-select:none;pointer-events:auto;
        z-index:10;position:relative;
        backdrop-filter:blur(4px);
        transition:transform 0.15s ease, box-shadow 0.15s ease;
      `
      el.innerHTML = icon
      el.onclick = (e) => {
        e.stopPropagation()
        handlePointClick(pt)
      }
      el.onmouseenter = () => {
        el.style.transform = 'scale(1.15)'
        el.style.boxShadow = `0 0 16px ${color}aa,0 0 40px ${color}70`
      }
      el.onmouseleave = () => {
        el.style.transform = 'scale(1)'
        el.style.boxShadow = `0 0 12px ${color}80,0 0 30px ${color}40`
      }
      return el
    },
    [getTooltipTextForIcon, handlePointClick]
  )

  // HTML icon markers removed: we now render cyber events as glowing dots again

  const flightLabel = useCallback((d: object) => {
    const fp = d as FlightPoint
    const callsign = fp.callsign || fp.icao24
    const country = fp.origin_country ? ` · ${fp.origin_country}` : ''
    const military = isMilitaryAircraft(fp)
    const color = military ? MILITARY_FLIGHT_COLOR : FLIGHT_COLOR
    return `<div style="background:rgba(0,0,0,0.95);color:#fff;padding:8px 12px;border-radius:4px;font-size:10px;border:1px solid ${color};font-family:monospace">
      <div style="color:${color};font-weight:700">${military ? 'MILITARY AIRCRAFT' : 'AIRCRAFT'} ✈</div>
      <div>${callsign}${country}</div>
    </div>`
  }, [])

  const arcTypeLabel = useCallback((d: object) => {
    const a = d as ArcData
    const typeLabels: Record<string, string> = {
      ransomware: '🔒 RANSOMWARE',
      vulnerability: '⚠ VULNERABILITY',
      cyber_attack: '⚡ CYBER ATTACK',
      conflict: '💥 CONFLICT',
      disaster: '🔥 DISASTER',
      gps_spoofing: '📵 GPS SPOOFING',
    }
    const label = typeLabels[a.eventType] || '📡 EVENT'
    return `<div style="background:rgba(0,0,0,0.95);color:${a.color};padding:8px 12px;border-radius:4px;font-size:10px;border:1px solid ${a.color}60;box-shadow:0 0 20px ${a.color}30;max-width:260px;font-family:monospace">
      <div style="font-weight:700;letter-spacing:1px;font-size:9px">${label}</div>
      <div style="color:#e2e8f0;opacity:0.85;margin-top:4px;font-family:sans-serif;font-size:11px">${a.label.slice(0, 100)}</div>
    </div>`
  }, [])

  return (
    <Globe
      ref={globeRef}
      rendererConfig={{ antialias: true, alpha: true }}
      globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
      bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
      backgroundImageUrl="//unpkg.com/three-globe/example/img/night-sky.png"
      showAtmosphere={true}
      atmosphereColor="#0ea5e9"
      atmosphereAltitude={0.22}
      pointsData={allPoints}
      pointLat={(d) => (d as GlobePoint).latitude}
      pointLng={(d) => (d as GlobePoint).longitude}
      pointColor={getColor}
      pointRadius={getRadius}
      pointAltitude={(d) => {
        const pt = d as GlobePoint
        if (pt && 'type' in pt && pt.type === 'gpsjam') return 0.002
        if (pt && 'type' in pt && pt.type === 'flight') return 0.025
        const ep = pt as EnrichedPoint
        if (ep?.severity === 'critical') return 0.04
        if (ep?.severity === 'high') return 0.025
        return 0.018
      }}
      pointLabel={getLabel}
      onPointClick={handlePointClick}
      pointsMerge={false}
      onGlobeClick={({ lat, lng }) => {
        if (!onCountryClick) return
        const code = getNearestCountryCode(lat, lng)
        if (code) onCountryClick(code)
      }}
      htmlElementsData={allPoints.filter((pt) => 'type' in pt && (pt.type === 'flight' || pt.type === 'vessel' || pt.type === 'gamma' || pt.type === 'base'))}
      htmlLat={(d) => (d as GlobePoint).latitude}
      htmlLng={(d) => (d as GlobePoint).longitude}
      htmlAltitude={(d) => {
        const pt = d as GlobePoint
        if (pt && 'type' in pt && pt.type === 'flight') return 0.03
        if (pt && 'type' in pt && (pt.type === 'vessel' || pt.type === 'gamma' || pt.type === 'base')) return 0.02
        return 0.02
      }}
      htmlElement={(d) => createIconElement(d) as HTMLElement}
      htmlTransitionDuration={0}
      arcsData={[...arcs, ...flightArcs, ...gpsSpoofingArcs]}
      arcStartLat={(d: object) => (d as ArcData).startLat}
      arcStartLng={(d: object) => (d as ArcData).startLng}
      arcEndLat={(d: object) => (d as ArcData).endLat}
      arcEndLng={(d: object) => (d as ArcData).endLng}
      arcColor={(d: object) => [(d as ArcData).color, (d as ArcData).color2]}
      arcStroke={(d: object) => (d as ArcData).stroke}
      arcDashLength={(d: object) => (d as ArcData).dashLen}
      arcDashGap={(d: object) => (d as ArcData).dashGap}
      arcDashAnimateTime={(d: object) => (d as ArcData).animateTime}
      arcLabel={arcTypeLabel}
      onArcClick={(d: object) => {
        const a = d as ArcData
        if (a.event) {
          onEventClick(a.event)
        }
      }}
      arcAltitudeAutoScale={0.3}
      ringsData={rings}
      ringLat={(d: object) => (d as RingData).lat}
      ringLng={(d: object) => (d as RingData).lng}
      ringMaxRadius={(d: object) => (d as RingData).maxR}
      ringPropagationSpeed={(d: object) => (d as RingData).propagationSpeed}
      ringRepeatPeriod={(d: object) => (d as RingData).repeatPeriod}
      ringColor={(d: object) => () => (d as RingData).color}
      ringAltitude={0.005}
      heatmapsData={gpsJamHeatmapData}
      heatmapPoints={(d: object) => (d as { points: GpsJamPoint[] }).points}
      heatmapPointLat={(p: GpsJamPoint) => p.latitude}
      heatmapPointLng={(p: GpsJamPoint) => p.longitude}
      heatmapPointWeight={(p: GpsJamPoint) => Math.max(0.1, (p.ratio_bad || 0) * 15 + (p.n_bad || 0) * 0.02)}
      heatmapBandwidth={2.2}
      heatmapColorFn={gpsJamHeatmapColorFn}
      heatmapColorSaturation={1.8}
      heatmapBaseAltitude={0.001}
      heatmapTopAltitude={0.015}
      heatmapsTransitionDuration={800}
      width={undefined}
      height={undefined}
    />
  )
}

export { ARC_TYPE_COLORS }
